// Plain constants, importable without pulling Zod (used by the web app).

export const PLATFORMS = ["ios", "android"] as const;
type PlatformName = (typeof PLATFORMS)[number];

/** Roles in an organization: owners and admins manage it (credentials, apps, members); members only see their apps. */
export const ORG_ROLES = ["owner", "admin", "member"] as const;
export type OrgRoleName = (typeof ORG_ROLES)[number];

/** Paid features (Enterprise license on self-hosted instances, subscription plans on Shipyard Cloud). */
export const FEATURES = ["multi_org", "sso", "white_label", "audit_log", "audit_export", "approvals"] as const;
export type FeatureName = (typeof FEATURES)[number];

/** Self-hosted: Community (free) or Enterprise (license). Cloud: free / pro / business. */
export const PLANS = ["community", "enterprise", "free", "pro", "business"] as const;
export type PlanName = (typeof PLANS)[number];

/** Logos of white-label public pages (no SVG: it could run scripts on the instance's origin). */
export const LOGO_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;
export const LOGO_MAX_BYTES = 256 * 1024;

/** Shipyard Cloud plans: features and limits (null = unlimited; paid seats follow the subscription). */
export const CLOUD_PLANS = ["free", "pro", "business"] as const;
export type CloudPlanName = (typeof CLOUD_PLANS)[number];

export interface PlanLimits {
  seats: number | null;
  apps: number | null;
  buildsPerMonth: number | null;
  storageBytes: number | null;
  /** Audit log history shown (older entries are kept, hidden); null = complete. */
  auditRetentionDays: number | null;
}

const GB = 1024 ** 3;

export const CLOUD_PLAN_FEATURES: Record<CloudPlanName, readonly FeatureName[]> = {
  free: [],
  pro: ["white_label", "audit_log"],
  business: ["white_label", "audit_log", "audit_export", "sso", "approvals"],
};

export const CLOUD_PLAN_LIMITS: Record<CloudPlanName, PlanLimits> = {
  free: { seats: 2, apps: 2, buildsPerMonth: 30, storageBytes: 2 * GB, auditRetentionDays: 0 },
  pro: { seats: null, apps: null, buildsPerMonth: 500, storageBytes: 50 * GB, auditRetentionDays: 30 },
  business: { seats: null, apps: null, buildsPerMonth: null, storageBytes: 200 * GB, auditRetentionDays: 365 },
};

export const BILLING_INTERVALS = ["month", "year"] as const;
export type BillingInterval = (typeof BILLING_INTERVALS)[number];

/** Stripe subscription statuses. */
export const SUBSCRIPTION_STATUSES = ["active", "trialing", "past_due", "canceled", "incomplete", "incomplete_expired", "unpaid", "paused"] as const;
export type SubscriptionStatusName = (typeof SUBSCRIPTION_STATUSES)[number];

export const LICENSE_STATUSES = ["valid", "grace", "expired", "invalid"] as const;
export type LicenseStatusName = (typeof LICENSE_STATUSES)[number];

export const API_KEY_SCOPES = [
  "apps:read",
  "builds:read",
  "builds:write",
  "builds:promote",
  "notes:write",
  "keystore:read",
  "keystore:write",
  "admin",
] as const;

/** Preset colors offered by the tag color picker. */
export const TAG_COLORS = [
  "#ef4444",
  "#f97316",
  "#eab308",
  "#22c55e",
  "#14b8a6",
  "#0ea5e9",
  "#6366f1",
  "#a855f7",
  "#ec4899",
  "#64748b",
] as const;

export const ANDROID_TRACKS = ["internal", "alpha", "beta", "production"] as const;

/** Store limits for release notes, in characters. */
export const NOTES_MAX_LENGTH: Record<PlatformName, Record<"testing" | "store", number>> = {
  ios: { testing: 4000, store: 4000 },
  android: { testing: 500, store: 500 },
};

/** Locales supported by App Store Connect (Apple codes), with an English label. */
export const STORE_LOCALES: { code: string; label: string }[] = [
  { code: "ar-SA", label: "Arabic" },
  { code: "ca", label: "Catalan" },
  { code: "cs", label: "Czech" },
  { code: "da", label: "Danish" },
  { code: "de-DE", label: "German" },
  { code: "el", label: "Greek" },
  { code: "en-AU", label: "English (Australia)" },
  { code: "en-CA", label: "English (Canada)" },
  { code: "en-GB", label: "English (U.K.)" },
  { code: "en-US", label: "English (U.S.)" },
  { code: "es-ES", label: "Spanish (Spain)" },
  { code: "es-MX", label: "Spanish (Mexico)" },
  { code: "fi", label: "Finnish" },
  { code: "fr-CA", label: "French (Canada)" },
  { code: "fr-FR", label: "French" },
  { code: "he", label: "Hebrew" },
  { code: "hi", label: "Hindi" },
  { code: "hr", label: "Croatian" },
  { code: "hu", label: "Hungarian" },
  { code: "id", label: "Indonesian" },
  { code: "it", label: "Italian" },
  { code: "ja", label: "Japanese" },
  { code: "ko", label: "Korean" },
  { code: "ms", label: "Malay" },
  { code: "nl-NL", label: "Dutch" },
  { code: "no", label: "Norwegian" },
  { code: "pl", label: "Polish" },
  { code: "pt-BR", label: "Portuguese (Brazil)" },
  { code: "pt-PT", label: "Portuguese (Portugal)" },
  { code: "ro", label: "Romanian" },
  { code: "ru", label: "Russian" },
  { code: "sk", label: "Slovak" },
  { code: "sv", label: "Swedish" },
  { code: "th", label: "Thai" },
  { code: "tr", label: "Turkish" },
  { code: "uk", label: "Ukrainian" },
  { code: "vi", label: "Vietnamese" },
  { code: "zh-Hans", label: "Chinese (Simplified)" },
  { code: "zh-Hant", label: "Chinese (Traditional)" },
];

/** Apple locale codes whose Google Play equivalent differs. */
const PLAY_LOCALES: Record<string, string> = {
  "ar-SA": "ar",
  cs: "cs-CZ",
  da: "da-DK",
  el: "el-GR",
  fi: "fi-FI",
  he: "iw-IL",
  hi: "hi-IN",
  hu: "hu-HU",
  it: "it-IT",
  ja: "ja-JP",
  ko: "ko-KR",
  no: "no-NO",
  pl: "pl-PL",
  ru: "ru-RU",
  sv: "sv-SE",
  tr: "tr-TR",
  "zh-Hans": "zh-CN",
  "zh-Hant": "zh-TW",
};

/** Converts a store locale (Apple code) to the code expected by Google Play. */
export function toPlayLocale(locale: string): string {
  return PLAY_LOCALES[locale] ?? locale;
}
